import * as Api from "@api";
import * as C from "@components";
import React from "react";
import css from "./customizations-gallery-card.module.scss";
import VadImage from "vadimages-nextjs-image-optimizer";

interface CustomizationsGalleryCardProps<Keys extends readonly string[], AltKeys extends readonly string[]>
{
    altKeys?: AltKeys;
    bgColors: { [key in Keys[number]]: C.Color | string; }
    keys: Keys;
    imageHeight?: number;
    imageWidth?: number;
    linkProps: Api.ContentItem;
    pageContent: Api.Content<Keys[number]>;
    thumbHeight: number;
    thumbWidth: number;
    titleProps: Api.ContentItem;
}

export const CustomizationsGalleryCard = <Keys extends readonly string[], AltKeys extends readonly string[]>({ altKeys, bgColors, imageHeight, imageWidth, keys, linkProps, pageContent, thumbHeight, thumbWidth, titleProps, ...props }: CustomizationsGalleryCardProps<Keys, AltKeys>): React.FunctionComponentElement<CustomizationsGalleryCardProps<Keys, AltKeys>> =>
{
    const imageDirectory = "/images/classic-customizations/";

    const items = keys.reduce((items, key) =>
    {
        items[`${key}.thumb`] = {
            galleryId: key,
            style: { ["--thumb-bg-color" as string]: bgColors[key as Keys[number]] }
        };

        return items;
    }, {} as Record<string, C.ImageGalleryThumb>);

    return (
        <C.Card {...props} className={css["card"]} data-logic-feature="image-gallery" layout={{ all: { width: C.Width.W100P } }} textColor={C.Color.BlackDomina} useWrapper>
            <C.PageContent {...titleProps} centered margins={{ bottom: C.Margin.D80 }} typography={C.Typography.H2} />
            <div className={css["content"]}>
                <C.ImageGalleryImagePane captionProps={{ centered: true, margins: { top: C.Margin.D24 }, typography: C.Typography.P5Caption }} className={css["image-pane"]} imageDirectory={imageDirectory} imageHeight={imageHeight} imageWidth={imageWidth} keys={keys} pageContent={pageContent} />
                <C.Column>
                    <C.ImageGalleryThumbPane className={css["thumb-pane"]} imageDirectory={imageDirectory} imageHeight={thumbHeight} imageWidth={thumbWidth} keys={keys.map(key => `${key}.thumb` as const)} items={items} />
                    <C.PageLink href="#contact-us" data-logic-feature="scroll-to" data-scroll-to="contact-us" margins={{ top: C.Margin.D24 }} >
                        <C.PageContent {...linkProps} typography={C.Typography.Button} />
                    </C.PageLink>
                </C.Column>
            </div>
            <div className={css["mobile-content"]}>
                {
                    altKeys?.map(key => <VadImage key={key} src={`${imageDirectory}${key}.png`} width={75} height={300} alt={key} />)
                }
            </div>
        </C.Card>
    );
};